// ConfigurationManagement.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import { Settings, Save, RefreshCw, CheckCircle, XCircle, AlertCircle, Plus, X, Users, Image, UploadCloud, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from './theme';
import { setClientIconUrl } from "../utils/clientIcon";




const ConfigurationManagement = () => {
  const { theme } = useContext(ThemeContext);
  const { profile } = useProfile();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [adding, setAdding] = useState(false);
  const [showAssignAdminModal, setShowAssignAdminModal] = useState(false);
  const [adminCandidates, setAdminCandidates] = useState([]);
  const [selectedAdminIds, setSelectedAdminIds] = useState([]);
  const [adminSearch, setAdminSearch] = useState("");
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [splashInfo, setSplashInfo] = useState(null);
  const [loadingSplash, setLoadingSplash] = useState(false);
  const [uploadingSplash, setUploadingSplash] = useState(false);
  const [splashUrlInput, setSplashUrlInput] = useState('');
  const [splashFile, setSplashFile] = useState(null);
  const [splashPreviewUrl, setSplashPreviewUrl] = useState(null);
  const [splashActive, setSplashActive] = useState(true);
  const [iconUrlInput, setIconUrlInput] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);
  const splashSectionRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const DotCheckbox = ({ checked, onChange }) => {
    const size = 18;
    const outerBorder = theme === "dark" ? "2px solid rgba(203,213,225,0.06)" : "2px solid rgba(100,116,139,0.4)";
    const bg = checked ? (theme === "dark" ? "#34d399" : "#059669") : "transparent";
    const innerSize = 8;

    const style = {
      width: size,
      height: size,
      minWidth: size,
      minHeight: size,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxSizing: "border-box",
      background: bg,
      border: checked ? "none" : outerBorder,
    };

    const inner = {
      width: innerSize,
      height: innerSize,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.95)",
    };

    return (
      <div
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onChange();
        }}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onChange();
          }
        }}
        style={style}
      >
        {checked && <div style={inner} />}
      </div>
    );
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    return () => {
      if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(splashPreviewUrl);
      }
    };
  }, [splashPreviewUrl]);

  useEffect(() => {
    return () => {
      if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(iconPreviewUrl);
      }
    };
  }, [iconPreviewUrl]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`${BASE_URL}/api/configurations/companies`, {
        method: "GET",
      });
      const data = await response.json();
      setCompanies(data?.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      showMessage('error', 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyPermissions = async (clientName) => {
    try {
      setLoading(true);
      resetSplashState();
      const response = await apiFetch(`${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}`, {
        method: "GET",
      });
      const data = await response.json();
      setPermissions(data?.data || null);
      setSelectedCompany(clientName);
      await fetchSplashData(clientName);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      showMessage('error', 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const resetSplashState = () => {
    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }
    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setSplashInfo(null);
    setSplashUrlInput('');
    setSplashFile(null);
    setSplashPreviewUrl(null);
    setSplashActive(true);
    setIconUrlInput('');
    setIconFile(null);
    setIconPreviewUrl(null);
  };

  const fetchSplashData = async (clientName) => {
    if (!clientName) {
      resetSplashState();
      return;
    }

    try {
      setLoadingSplash(true);
      const response = await apiFetch(`${BASE_URL}/api/client-splash/${encodeURIComponent(clientName)}`, {
        method: "GET",
      });

      if (response.ok) {
        const json = await response.json();
        const record = json?.data || null;
        setSplashInfo(record);
        setSplashActive(record?.isActive !== false);
        const urlValue = record?.fullUrl || record?.gifUrl || '';
        setSplashUrlInput(urlValue);
        setSplashPreviewUrl(urlValue || null);
        const iconValue = record?.iconFullUrl || record?.iconUrl || '';
        setIconUrlInput(iconValue);
        setIconPreviewUrl(iconValue || null);
        setIconFile(null);
      } else if (response.status === 404) {
        resetSplashState();
      } else {
        throw new Error('Unable to load splash screen details');
      }
    } catch (error) {
      console.error('Failed to fetch splash screen:', error);
      showMessage('error', 'Failed to load splash screen');
    } finally {
      setLoadingSplash(false);
      setSplashFile(null);
      setIconFile(null);
    }
  };

  const handleSave = async () => {
    if (!selectedCompany || !permissions) return;

    try {
      setSaving(true);
      const response = await apiFetch(
        `${BASE_URL}/api/configurations/${encodeURIComponent(selectedCompany)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(permissions),
        }
      );
      if (!response.ok) {
        showMessage('error', 'Failed to save configuration');
      } else {
        showMessage('success', 'Configuration saved successfully!');
      }
      fetchCompanies(); // Refresh list
    } catch (error) {
      console.error('Failed to save configuration:', error);
      showMessage('error', 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    setPermissions(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSplashFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (file.type !== 'image/gif') {
      showMessage('error', 'Only GIF files are allowed');
      event.target.value = '';
      return;
    }

    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setSplashFile(file);
    setSplashPreviewUrl(preview);
    setSplashUrlInput('');
  };

  const clearSplashFile = () => {
    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }
    setSplashFile(null);
    setSplashPreviewUrl(splashInfo?.fullUrl || splashInfo?.gifUrl || null);
    setSplashUrlInput(splashInfo?.fullUrl || splashInfo?.gifUrl || '');
  };

  const handleIconFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showMessage('error', 'Icon must be PNG, JPG, JPEG, SVG, or WEBP');
      event.target.value = '';
      return;
    }

    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setIconFile(file);
    setIconPreviewUrl(preview);
    setIconUrlInput('');
  };

  const clearIconFile = () => {
    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setIconFile(null);
    const fallback = splashInfo?.iconFullUrl || splashInfo?.iconUrl || '';
    setIconPreviewUrl(fallback || null);
    setIconUrlInput(fallback);
  };

  const handleSplashSave = async () => {
    if (!selectedCompany) {
      showMessage('error', 'Select a company first');
      return;
    }

    const trimmedGifUrl = splashUrlInput.trim();
    const trimmedIconUrl = iconUrlInput.trim();

    if (!splashFile && !trimmedGifUrl) {
      showMessage('error', 'Upload a GIF or provide an external URL');
      return;
    }

    try {
      setUploadingSplash(true);
      let response;

      const shouldUseFormData = Boolean(splashFile || iconFile);

      if (shouldUseFormData) {
        const formData = new FormData();
        formData.append('clientName', selectedCompany);
        formData.append('isActive', splashActive ? 'true' : 'false');
        if (splashFile) {
          formData.append('splashGif', splashFile);
        } else {
          formData.append('gifUrl', trimmedGifUrl);
        }
        if (iconFile) {
          formData.append('splashIcon', iconFile);
        } else {
          formData.append('iconUrl', trimmedIconUrl);
        }

        response = await apiFetch(`${BASE_URL}/api/client-splash`, {
          method: 'POST',
          body: formData,
        });
      } else {
        response = await apiFetch(`${BASE_URL}/api/client-splash`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientName: selectedCompany,
            gifUrl: trimmedGifUrl,
            iconUrl: trimmedIconUrl,
            isActive: splashActive,
          }),
        });
      }

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to save splash screen');
      }

      const json = await response.json();
      const record = json?.data || null;
      setSplashInfo(record);
      setSplashActive(record?.isActive !== false);
      const urlValue = record?.fullUrl || record?.gifUrl || '';
      setSplashUrlInput(urlValue);
      if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(splashPreviewUrl);
      }
      setSplashPreviewUrl(urlValue || null);
      setSplashFile(null);
      const iconValue = record?.iconFullUrl || record?.iconUrl || '';
      setIconUrlInput(iconValue);
      if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(iconPreviewUrl);
      }
      setIconPreviewUrl(iconValue || null);
      setIconFile(null);
      const loggedClientName = profile?.clientName || localStorage.getItem("clientName");
      if (
        loggedClientName &&
        selectedCompany &&
        loggedClientName.trim().toLowerCase() === selectedCompany.trim().toLowerCase()
      ) {
        setClientIconUrl(iconValue || null);
      }
      showMessage('success', 'Splash screen saved successfully');
    } catch (error) {
      console.error('Failed to save splash screen:', error);
      showMessage('error', error.message || 'Failed to save splash screen');
    } finally {
      setUploadingSplash(false);
    }
  };

  const handleSplashDeactivate = async () => {
    if (!selectedCompany) {
      showMessage('error', 'Select a company first');
      return;
    }

    try {
      setUploadingSplash(true);
      const response = await apiFetch(`${BASE_URL}/api/client-splash/${encodeURIComponent(selectedCompany)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to deactivate splash screen');
      }

      const json = await response.json();
      const record = json?.data || null;
      setSplashInfo(record);
      setSplashActive(record?.isActive !== false);
      const urlValue = record?.fullUrl || record?.gifUrl || '';
      setSplashUrlInput(urlValue);
      setSplashPreviewUrl(urlValue || null);
      setSplashFile(null);
      const iconValue = record?.iconFullUrl || record?.iconUrl || '';
      setIconUrlInput(iconValue);
      setIconPreviewUrl(iconValue || null);
      setIconFile(null);
      const loggedClientName = profile?.clientName || localStorage.getItem("clientName");
      if (
        loggedClientName &&
        selectedCompany &&
        loggedClientName.trim().toLowerCase() === selectedCompany.trim().toLowerCase()
      ) {
        setClientIconUrl(iconValue || null);
      }
      showMessage('success', 'Splash screen deactivated');
    } catch (error) {
      console.error('Failed to deactivate splash screen:', error);
      showMessage('error', error.message || 'Failed to deactivate splash screen');
    } finally {
      setUploadingSplash(false);
    }
  };

  const toggleSplashActive = () => {
    setSplashActive((prev) => !prev);
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) {
      showMessage('error', 'Please enter a company name');
      return;
    }

    try {
      setAdding(true);
      const response = await apiFetch(`${BASE_URL}/api/configurations/companies/new`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientName: newCompanyName.trim() }),
      });

      if (response.status === 409) {
        showMessage('error', 'Company already exists');
      } else if (!response.ok) {
        showMessage('error', 'Failed to create company');
      } else {
        showMessage('success', 'Company created successfully!');
        setShowAddModal(false);
        setNewCompanyName('');
        fetchCompanies(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to create company:', error);
      showMessage('error', 'Failed to create company');
    } finally {
      setAdding(false);
    }
  };

  const openAssignAdmin = async () => {
    if (!selectedCompany) {
      showMessage('error', 'Please select a company first');
      return;
    }

    setShowAssignAdminModal(true);
    setLoadingAdmins(true);
    setAdminSearch("");
    setSelectedAdminIds([]);

    try {
      const response = await apiFetch(`${BASE_URL}/employees/visibility?clientName=${encodeURIComponent(selectedCompany)}`, {
        method: "GET",
      });
      const data = await response.json();
      setAdminCandidates(data?.employees || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      showMessage('error', 'Failed to load employees');
      setAdminCandidates([]);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleToggleAdmin = (infinityCode) => {
    setSelectedAdminIds(prev =>
      prev.includes(infinityCode)
        ? prev.filter(id => id !== infinityCode)
        : [...prev, infinityCode]
    );
  };

  const handleSaveAdmins = async () => {
    if (selectedAdminIds.length === 0) {
      showMessage('error', 'Please select at least one employee');
      return;
    }

    setLoadingAdmins(true);
    try {
      const response = await apiFetch(`${BASE_URL}/employees/make-admin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          infinityCodes: selectedAdminIds,
          clientName: selectedCompany,
        }),
      });

      if (!response.ok) {
        showMessage('error', 'Failed to assign admins');
      } else {
        showMessage('success', 'Admins assigned successfully!');
      }
      setShowAssignAdminModal(false);
      setSelectedAdminIds([]);
    } catch (error) {
      console.error('Failed to assign admins:', error);
      showMessage('error', 'Failed to assign admins');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const styles = {
    container: {
      marginTop: '-30px',
    },
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "flex-start",  // 👈 Change from "center" to "flex-start"
      justifyContent: "center",
      paddingTop: "60px",  // 👈 Add this for spacing from top
      zIndex: 1000,
      overflowY: "auto",  // 👈 Add this to allow scrolling if content is tall
    },

    modalContent: {
      background: theme === "light" ? "#ffffff" : "#1e293b",
      borderRadius: "16px",
      padding: "32px",
      width: "90%",
      maxWidth: "500px",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    },

    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },

    modalTitle: {
      fontSize: "20px",
      fontWeight: "700",
      color: theme === "light" ? "#0f172a" : "#ffffff",
    },

    closeButton: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      padding: "4px",
      color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)",
    },

    input: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "8px",
      border: theme === "light" ? "2px solid #e2e8f0" : "2px solid rgba(255, 255, 255, 0.15)",
      background: theme === "light" ? "#ffffff" : "rgba(255, 255, 255, 0.05)",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      fontSize: "14px",
      marginBottom: "24px",
    },

    modalButtons: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
    },

    cancelButton: {
      background: theme === "light" ? "#e2e8f0" : "rgba(255, 255, 255, 0.1)",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      border: "none",
      borderRadius: "8px",
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
    },

    submitButton: {
      background: "linear-gradient(135deg, #10b981, #059669)",
      color: "white",
      border: "none",
      borderRadius: "8px",
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },

    card: {
      background: theme === "light"
        ? "rgba(255, 255, 255, 0.9)"
        : "rgba(255, 255, 255, 0.08)",
      backdropFilter: "blur(20px)",
      border: theme === "light"
        ? "1px solid rgba(0, 0, 0, 0.1)"
        : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "16px",
      padding: "24px",
      marginBottom: "24px",
      marginLeft: 20,
    },

    title: {
      fontSize: "24px",
      fontWeight: "700",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      margin: "0 0 20px 0",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },

    gridContainer: {
      display: "grid",
      gridTemplateColumns: "300px 1fr",
      gap: "24px",
    },

    companyList: {
      maxHeight: "600px",
      overflowY: "auto",
      paddingRight: "8px",
    },

    companyCard: (isSelected) => ({
      padding: "16px",
      border: isSelected
        ? "2px solid #2e7cba"
        : theme === "light"
          ? "2px solid #e2e8f0"
          : "2px solid rgba(255, 255, 255, 0.15)",
      borderRadius: "12px",
      marginBottom: "12px",
      background: isSelected
        ? theme === "light"
          ? "rgba(46, 124, 186, 0.1)"
          : "rgba(46, 124, 186, 0.2)"
        : theme === "light"
          ? "#ffffff"
          : "rgba(255, 255, 255, 0.02)",
      cursor: "pointer",
      transition: "all 0.3s ease",
    }),

    companyName: {
      fontSize: "16px",
      fontWeight: "600",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      marginBottom: "4px",
    },

    companyStatus: (isActive) => ({
      fontSize: "12px",
      color: isActive ? "#10b981" : "#ef4444",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    }),

    permissionsContainer: {
      background: theme === "light"
        ? "#ffffff"
        : "rgba(255, 255, 255, 0.02)",
      border: theme === "light"
        ? "2px solid #e2e8f0"
        : "2px solid rgba(255, 255, 255, 0.15)",
      borderRadius: "12px",
      padding: "24px",
    },

    sectionTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      marginBottom: "16px",
      marginTop: "24px",
      paddingBottom: "8px",
      borderBottom: theme === "light"
        ? "2px solid #e2e8f0"
        : "2px solid rgba(255, 255, 255, 0.15)",
    },

    permissionItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 16px",
      marginBottom: "8px",
      borderRadius: "8px",
      background: theme === "light"
        ? "rgba(0, 0, 0, 0.02)"
        : "rgba(255, 255, 255, 0.05)",
    },

    permissionLabel: {
      fontSize: "14px",
      fontWeight: "500",
      color: theme === "light" ? "#374151" : "rgba(255, 255, 255, 0.9)",
    },

    toggle: (isEnabled) => ({
      width: "48px",
      height: "24px",
      borderRadius: "12px",
      background: isEnabled
        ? "linear-gradient(135deg, #2e7cba, #2e7cba)"
        : theme === "light"
          ? "#cbd5e1"
          : "rgba(255, 255, 255, 0.2)",
      position: "relative",
      cursor: "pointer",
      transition: "all 0.3s ease",
    }),

    toggleKnob: (isEnabled) => ({
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      background: "#ffffff",
      position: "absolute",
      top: "2px",
      left: isEnabled ? "26px" : "2px",
      transition: "all 0.3s ease",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
    }),

    button: {
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "12px 24px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginTop: "24px",
    },

    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
      color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)",
    },

    message: (type) => ({
      padding: "12px 16px",
      borderRadius: "8px",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: type === 'success'
        ? "rgba(16, 185, 129, 0.1)"
        : type === 'error'
          ? "rgba(239, 68, 68, 0.1)"
          : "rgba(251, 191, 36, 0.1)",
      border: type === 'success'
        ? "1px solid #10b981"
        : type === 'error'
          ? "1px solid #ef4444"
          : "1px solid #fbbf24",
      color: type === 'success'
        ? "#10b981"
        : type === 'error'
          ? "#ef4444"
          : "#fbbf24",
    }),
    splashCard: {
      background: theme === "light" ? "rgba(14, 116, 144, 0.05)" : "rgba(14, 116, 144, 0.15)",
      border: theme === "light" ? "1px solid rgba(14, 116, 144, 0.2)" : "1px solid rgba(56, 189, 248, 0.25)",
      borderRadius: "12px",
      padding: "16px",
      marginBottom: "24px",
    },
    splashHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
      gap: "12px",
    },
    splashPreview: {
      width: "100%",
      maxHeight: "200px",
      objectFit: "contain",
      borderRadius: "12px",
      background: theme === "light" ? "rgba(255, 255, 255, 0.8)" : "rgba(15, 23, 42, 0.6)",
      border: theme === "light" ? "1px solid rgba(14, 116, 144, 0.2)" : "1px solid rgba(56, 189, 248, 0.2)",
      padding: "8px",
    },
    splashInputRow: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "12px",
    },
    splashInput: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: "8px",
      border: theme === "light" ? "1px solid #94a3b8" : "1px solid rgba(148, 163, 184, 0.4)",
      background: theme === "light" ? "#ffffff" : "rgba(15, 23, 42, 0.5)",
      color: theme === "light" ? "#0f172a" : "#ffffff",
      fontSize: "14px",
    },
    iconContainer: {
      marginTop: "24px",
      paddingTop: "16px",
      borderTop: theme === "light" ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid rgba(148, 163, 184, 0.25)",
    },
    iconHeaderRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px",
    },
    iconTitle: (currentTheme) => ({
      fontSize: "15px",
      fontWeight: 600,
      color: currentTheme === "light" ? "#0f172a" : "#ffffff",
      margin: 0,
    }),
    iconSubtitle: (currentTheme) => ({
      fontSize: "12px",
      color: currentTheme === "light" ? "#475569" : "rgba(255, 255, 255, 0.7)",
      margin: "4px 0 0 0",
    }),
    iconPreview: {
      width: "72px",
      height: "72px",
      borderRadius: "16px",
      background: theme === "light" ? "rgba(255, 255, 255, 0.8)" : "rgba(15, 23, 42, 0.6)",
      border: theme === "light" ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid rgba(148, 163, 184, 0.3)",
      padding: "12px",
      objectFit: "contain",
      boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",
    },
    iconPlaceholder: (currentTheme) => ({
      width: "72px",
      height: "72px",
      borderRadius: "16px",
      border: "1px dashed rgba(148, 163, 184, 0.4)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      fontSize: "12px",
      color: currentTheme === "light" ? "#1157ba" : "rgba(255, 255, 255, 0.6)",
      background: currentTheme === "light" ? "rgba(255, 255, 255, 0.6)" : "rgba(15, 23, 42, 0.3)",
    }),
    iconInputRow: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "12px",
    },
    iconLabel: (currentTheme) => ({
      display: "block",
      fontSize: "12px",
      fontWeight: 600,
      color: currentTheme === "light" ? "#0f172a" : "rgba(255, 255, 255, 0.85)",
      marginBottom: "6px",
    }),
    iconHint: (currentTheme) => ({
      fontSize: "12px",
      color: currentTheme === "light" ? "#475569" : "rgba(255, 255, 255, 0.6)",
      marginTop: "12px",
    }),
    splashActions: {
      display: "flex",
      flexWrap: "wrap",
      gap: "12px",
      alignItems: "center",
      marginTop: "16px",
    },
    secondaryButton: {
      background: theme === "light" ? "rgba(148, 163, 184, 0.15)" : "rgba(148, 163, 184, 0.1)",
      color: theme === "light" ? "#1f2937" : "rgba(255, 255, 255, 0.85)",
      border: "none",
      borderRadius: "8px",
      padding: "10px 16px",
      fontSize: "13px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  const moduleCategories = {
    "Core HR Modules": [
      { key: "employeeManagement", label: "Employee Management" },
      { key: "attendanceManagement", label: "Attendance Management" },
      { key: "leaveManagement", label: "Leave Management" },
    ],
    "Payroll Modules": [
      { key: "payrollManagement", label: "Payroll Management" },
      { key: "advancementRequests", label: "Advancement Requests" },
      { key: "salarySlips", label: "Salary Slips" },
    ],
    "Document Modules": [
      { key: "documentRequests", label: "Document Requests" },
      { key: "documentGeneration", label: "Document Generation" },
      { key: "employeeDocuments", label: "Employee Documents" },
    ],
    "Communication Modules": [
      { key: "communicatorModule", label: "Communicator Module (PTT & Private Messages)" },
      { key: "pttChannels", label: "PTT Channels" },
      { key: "privateMessaging", label: "Private Messaging" },
    ],
    "Calendar & Events": [
      { key: "calendarModule", label: "Calendar Module" },
      { key: "eventManagement", label: "Event Management" },
    ],
    "Employee Actions": [
      { key: "employeeActions", label: "Employee Actions" },
      { key: "promotions", label: "Promotions" },
      { key: "transfers", label: "Transfers" },
      { key: "terminations", label: "Terminations" },
    ],
    "Reports & Analytics": [
      { key: "attendanceReports", label: "Attendance Reports" },
      { key: "payrollReports", label: "Payroll Reports" },
      { key: "leaveReports", label: "Leave Reports" },
      { key: "customReports", label: "Custom Reports" },
    ],
    "Advanced Features": [
      { key: "biometricIntegration", label: "Biometric Integration" },
      { key: "apiAccess", label: "API Access" },
      { key: "bulkOperations", label: "Bulk Operations" },
      { key: "dataExport", label: "Data Export" },
      { key: "dataImport", label: "Data Import" },
    ],
    "Admin Features": [
      { key: "userManagement", label: "User Management" },
      { key: "roleManagement", label: "Role Management" },
      { key: "systemSettings", label: "System Settings" },
      { key: "auditLogs", label: "Audit Logs" },
    ],
  };

  return (

    <div style={styles.container}>
      {/* Add Company Modal */}
      {showAddModal && (
        <div style={styles.modal} onClick={() => setShowAddModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Add New Company</h3>
              <button style={styles.closeButton} onClick={() => setShowAddModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div>
              <label style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: theme === "light" ? "#374151" : "rgba(255, 255, 255, 0.9)",
                marginBottom: "8px",
              }}>
                Company Name
              </label>
              <input
                type="text"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="Enter company name..."
                style={styles.input}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !adding) {
                    handleAddCompany();
                  }
                }}
              />
            </div>

            <div style={styles.modalButtons}>
              <button
                style={styles.cancelButton}
                onClick={() => setShowAddModal(false)}
                disabled={adding}
              >
                Cancel
              </button>
              <button
                style={styles.submitButton}
                onClick={handleAddCompany}
                disabled={adding}
              >
                {adding ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Create Company
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={styles.card}>

        <div style={styles.title}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Settings size={24} />
            Company Module Configuration                             
          </div>
          <button
            style={{
              background: "linear-gradient(135deg, #10b981, #059669)",
              color: "white",
              marginLeft: "auto",
              border: "none",
              borderRadius: "10px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={18} />
            Add Company
          </button>
        </div>

        {message.text && (
          <div style={styles.message(message.type)}>
            {message.type === 'success' ? <CheckCircle size={20} /> :
              message.type === 'error' ? <XCircle size={20} /> :
                <AlertCircle size={20} />}
            {message.text}
          </div>
        )}


        {loading && !permissions ? (
          <div style={styles.emptyState}>
            <RefreshCw size={64} style={{ marginBottom: '20px', opacity: 0.3, animation: 'spin 2s linear infinite' }} />
            <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}>
              Loading...
            </h3>
          </div>
        ) : (
          <div style={styles.gridContainer}>
            {/* Company List */}
            <div style={styles.companyList}>
              <h3 style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme === "light" ? "#0f172a" : "#ffffff",
                marginBottom: "16px",
              }}>
                Companies ({companies.length})
              </h3>
              {companies.map(company => (
                <div
                  key={company.clientName}
                  style={styles.companyCard(selectedCompany === company.clientName)}
                  onClick={() => fetchCompanyPermissions(company.clientName)}
                  onMouseEnter={(e) => {
                    if (selectedCompany !== company.clientName) {
                      e.currentTarget.style.borderColor = "#2e7cba";
                      e.currentTarget.style.transform = "translateX(4px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCompany !== company.clientName) {
                      e.currentTarget.style.borderColor = theme === "light" ? "#e2e8f0" : "rgba(255, 255, 255, 0.15)";
                      e.currentTarget.style.transform = "translateX(0)";
                    }
                  }}
                >
                  <div style={styles.companyName}>{company.clientName}</div>
                  <div style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    marginTop: "4px",
                  }}>
                    <div style={styles.companyStatus(company.isActive)}>
                      {company.isActive ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {company.isActive ? 'Active' : 'Inactive'}
                    </div>
                    {company.subscriptionEndDate && (
                      <span style={{
                        fontSize: "11px",
                        color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.5)",
                      }}>
                        Until: {new Date(company.subscriptionEndDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>



            {/* Permissions Panel */}
            {!permissions ? (
              <div style={styles.emptyState}>
                <Settings size={64} style={{ marginBottom: '20px', opacity: 0.3 }} />
                <h3 style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: theme === "light" ? "#374151" : "rgba(255, 255, 255, 0.8)"
                }}>
                  Select a Company
                </h3>
                <p>Choose a company from the list to configure modules</p>
              </div>
            ) : (
              <div style={styles.permissionsContainer}>
                <h3 style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: theme === "light" ? "#0f172a" : "#ffffff",
                  marginBottom: "8px",
                }}>
                  {selectedCompany}
                </h3>
                <p style={{
                  fontSize: "14px",
                  color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
                  marginBottom: "24px",
                }}>
                  Enable or disable modules for this company
                </p>
                {/* ADD ALL THIS CODE HERE ↓ */}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
                  <button
                    style={{
                      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onClick={() => navigate('/DashboardSuper/AdminAccountCreation')}
                  >
                    <Users size={18} />
                    Create Admin Account
                  </button>
                  <button
                    style={{
                      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onClick={openAssignAdmin}
                  >
                    <Users size={18} />
                    Assign Admin
                  </button>
                  <button
                    style={{
                      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onClick={() => splashSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  >
                    <Image size={18} />
                    Manage Splash GIF
                  </button>
                </div>

                <div
                  ref={splashSectionRef}
                  style={styles.splashCard}
                >
                  <div style={styles.splashHeader}>
                    <div>
                      <h4 style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: theme === "light" ? "#0f172a" : "#ffffff",
                        margin: 0,
                        marginBottom: "4px",
                      }}>
                        Client Splash Screen
                      </h4>
                      <p style={{
                        fontSize: "12px",
                        color: theme === "light" ? "#475569" : "rgba(255, 255, 255, 0.7)",
                        margin: 0,
                      }}>
                        Displayed after login for {selectedCompany}
                      </p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: splashActive
                          ? theme === "light" ? "#0f172a" : "rgba(255, 255, 255, 0.85)"
                          : "#ef4444",
                      }}>
                        {splashActive ? 'Active' : 'Inactive'}
                      </span>
                      <div
                        style={styles.toggle(splashActive)}
                        onClick={toggleSplashActive}
                      >
                        <div style={styles.toggleKnob(splashActive)} />
                      </div>
                    </div>
                  </div>

                  {loadingSplash ? (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      padding: "24px",
                      color: theme === "light" ? "#0f172a" : "rgba(255, 255, 255, 0.7)",
                    }}>
                      <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                      Loading splash details...
                    </div>
                  ) : (
                    <div style={{ marginBottom: "16px" }}>
                      {splashPreviewUrl ? (
                        <img
                          src={splashPreviewUrl}
                          alt="Splash preview"
                          style={styles.splashPreview}
                        />
                      ) : (
                        <div style={{
                          ...styles.splashPreview,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme === "light" ? "#0f172a" : "rgba(255, 255, 255, 0.6)",
                          gap: "8px",
                          fontSize: "13px",
                        }}>
                          <Image size={28} />
                          No splash GIF configured
                        </div>
                      )}
                    </div>
                  )}

                  <div style={styles.splashInputRow}>
                    <div>
                      <label style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: theme === "light" ? "#0f172a" : "rgba(255, 255, 255, 0.85)",
                        marginBottom: "6px",
                      }}>
                        Upload GIF file
                      </label>
                      <input
                        type="file"
                        accept="image/gif"
                        onChange={handleSplashFileChange}
                        disabled={uploadingSplash}
                        style={{ ...styles.splashInput, padding: "8px" }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: theme === "light" ? "#0f172a" : "rgba(255, 255, 255, 0.85)",
                        marginBottom: "6px",
                      }}>
                        Or link to an external GIF
                      </label>
                      <input
                        type="text"
                        value={splashUrlInput}
                        onChange={(e) => setSplashUrlInput(e.target.value)}
                        placeholder="https://example.com/splash.gif"
                        style={styles.splashInput}
                        disabled={uploadingSplash}
                      />
                    </div>
                  </div>

                  <div style={{
                    fontSize: "12px",
                    color: theme === "light" ? "#475569" : "rgba(255, 255, 255, 0.6)",
                    marginTop: "12px",
                  }}>
                    Only GIF files are supported. Uploading a file will override the URL.
                  </div>

                  <div style={styles.iconContainer}>
                    <div style={styles.iconHeaderRow}>
                      <div>
                        <h5 style={styles.iconTitle(theme)}>
                          Client Badge Icon
                        </h5>
                        <p style={styles.iconSubtitle(theme)}>
                          Appears at the bottom-right badge for {selectedCompany}
                        </p>
                      </div>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      {iconPreviewUrl ? (
                        <img
                          src={iconPreviewUrl}
                          alt="Icon preview"
                          style={styles.iconPreview}
                        />
                      ) : (
                        <div style={styles.iconPlaceholder(theme)}>
                          <Image size={22} />
                          No icon selected
                        </div>
                      )}
                    </div>

                    <div style={styles.iconInputRow}>
                      <div>
                        <label style={styles.iconLabel(theme)}>
                          Upload icon file
                        </label>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                          onChange={handleIconFileChange}
                          disabled={uploadingSplash}
                          style={{ ...styles.splashInput, padding: "8px" }}
                        />
                      </div>
                      <div>
                        <label style={styles.iconLabel(theme)}>
                          Or link to an external icon
                        </label>
                        <input
                          type="text"
                          value={iconUrlInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            setIconUrlInput(value);
                            if (!iconFile) {
                              const trimmed = value.trim();
                              setIconPreviewUrl(trimmed ? trimmed : null);
                            }
                          }}
                          placeholder="https://example.com/icon.png"
                          style={styles.splashInput}
                          disabled={uploadingSplash}
                        />
                      </div>
                    </div>

                    <div style={styles.iconHint(theme)}>
                      PNG, JPG, JPEG, SVG, WEBP, or ICO up to 5MB. Leave blank to remove the icon.
                    </div>
                  </div>

                  <div style={styles.splashActions}>
                    <button
                      style={{ ...styles.button, marginTop: 0, padding: "10px 20px" }}
                      onClick={handleSplashSave}
                      disabled={uploadingSplash || loadingSplash}
                    >
                      {uploadingSplash ? (
                        <>
                          <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                          Saving...
                        </>
                      ) : (
                        <>
                          <UploadCloud size={18} />
                          Save Splash & Icon
                        </>
                      )}
                    </button>
                    {(splashFile || splashUrlInput || iconFile || iconUrlInput) && (
                      <button
                        style={styles.secondaryButton}
                        onClick={() => {
                          clearSplashFile();
                          clearIconFile();
                        }}
                        disabled={uploadingSplash}
                      >
                        <Trash2 size={16} />
                        Clear Selection
                      </button>
                    )}
                    {splashInfo && splashInfo.isActive && (
                      <button
                        style={{
                          ...styles.secondaryButton,
                          background: "rgba(239, 68, 68, 0.15)",
                          color: "#ef4444",
                        }}
                        onClick={handleSplashDeactivate}
                        disabled={uploadingSplash || loadingSplash}
                      >
                        <XCircle size={16} />
                        Deactivate Current GIF
                      </button>
                    )}
                  </div>
                </div>

                <div style={{
                  background: theme === "light" ? "rgba(46, 124, 186, 0.05)" : "rgba(46, 124, 186, 0.1)",
                  border: theme === "light" ? "1px solid #2e7cba" : "1px solid rgba(46, 124, 186, 0.3)",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "24px",
                }}>
                  <h4 style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: theme === "light" ? "#0f172a" : "#ffffff",
                    marginBottom: "16px",
                  }}>
                    Subscription Details
                  </h4>

                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "16px",
                  }}>
                    <div>
                      <label style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
                        marginBottom: "8px",
                      }}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={permissions.subscriptionStartDate ? new Date(permissions.subscriptionStartDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => setPermissions(prev => ({
                          ...prev,
                          subscriptionStartDate: e.target.value ? new Date(e.target.value) : null
                        }))}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: theme === "light" ? "1px solid #cbd5e1" : "1px solid rgba(255, 255, 255, 0.2)",
                          background: theme === "light" ? "#ffffff" : "rgba(255, 255, 255, 0.05)",
                          color: theme === "light" ? "#0f172a" : "#ffffff",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
                        marginBottom: "8px",
                      }}>
                        End Date
                      </label>
                      <input
                        type="date"
                        value={permissions.subscriptionEndDate ? new Date(permissions.subscriptionEndDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => setPermissions(prev => ({
                          ...prev,
                          subscriptionEndDate: e.target.value ? new Date(e.target.value) : null
                        }))}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: theme === "light" ? "1px solid #cbd5e1" : "1px solid rgba(255, 255, 255, 0.2)",
                          background: theme === "light" ? "#ffffff" : "rgba(255, 255, 255, 0.05)",
                          color: theme === "light" ? "#0f172a" : "#ffffff",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
                        marginBottom: "8px",
                      }}>
                        Subscription Tier
                      </label>
                      <select
                        value={permissions.subscriptionTier || 'basic'}
                        onChange={(e) => setPermissions(prev => ({
                          ...prev,
                          subscriptionTier: e.target.value
                        }))}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: theme === "light" ? "1px solid #cbd5e1" : "1px solid rgba(255, 255, 255, 0.2)",
                          background: theme === "light" ? "#ffffff" : "rgba(255, 255, 255, 0.05)",
                          color: theme === "light" ? "#0f172a" : "#ffffff",
                          fontSize: "14px",
                        }}
                      >
                        <option value="basic">Basic</option>
                        <option value="standard">Standard</option>
                        <option value="premium">Premium</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                  </div>

                  {permissions.subscriptionEndDate && new Date(permissions.subscriptionEndDate) < new Date() && (
                    <div style={{
                      marginTop: "12px",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      background: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid #ef4444",
                      color: "#ef4444",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}>
                      <AlertCircle size={16} />
                      Subscription has expired
                    </div>
                  )}

                  {/* Assign Admin Modal */}
                  {showAssignAdminModal && (
                    <div style={styles.modal} onClick={() => setShowAssignAdminModal(false)}>
                      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                          <h3 style={styles.modalTitle}>Assign Admins - {selectedCompany}</h3>
                          <button style={styles.closeButton} onClick={() => setShowAssignAdminModal(false)}>
                            <X size={24} />
                          </button>
                        </div>

                        <input
                          type="text"
                          value={adminSearch}
                          onChange={(e) => setAdminSearch(e.target.value)}
                          placeholder="Search employees by name..."
                          style={styles.input}
                        />

                        {loadingAdmins ? (
                          <div style={{ textAlign: "center", padding: "40px", color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)" }}>
                            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
                            <p>Loading employees...</p>
                          </div>
                        ) : (
                          <div style={{
                            maxHeight: "50vh",
                            overflowY: "auto",
                            padding: "16px",
                            background: theme === "light" ? "rgba(0, 0, 0, 0.02)" : "rgba(255, 255, 255, 0.05)",
                            borderRadius: "8px",
                            marginBottom: "20px"
                          }}>
                            {adminCandidates
                              .filter(emp => !adminSearch || emp.sr.toLowerCase().includes(adminSearch.toLowerCase()))
                              .map(emp => (
                                <label
                                  key={emp.infinityCode}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "10px 12px",
                                    cursor: "pointer",
                                    borderRadius: "8px",
                                    transition: "background 0.2s",
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = theme === "dark" ? "rgba(148, 163, 184, 0.08)" : "rgba(15, 23, 42, 0.03)"}
                                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                  <DotCheckbox
                                    checked={selectedAdminIds.includes(emp.infinityCode)}
                                    onChange={() => handleToggleAdmin(emp.infinityCode)}
                                  />
                                  <span style={{ fontSize: "14px", color: theme === "light" ? "#0f172a" : "#ffffff" }}>
                                    {emp.sr}
                                  </span>
                                </label>
                              ))}
                            {adminCandidates.filter(emp => !adminSearch || emp.sr.toLowerCase().includes(adminSearch.toLowerCase())).length === 0 && (
                              <p style={{ textAlign: "center", color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)" }}>
                                No employees found
                              </p>
                            )}
                          </div>
                        )}

                        <div style={styles.modalButtons}>
                          <button
                            style={styles.cancelButton}
                            onClick={() => setShowAssignAdminModal(false)}
                          >
                            Cancel
                          </button>
                          <button
                            style={styles.submitButton}
                            onClick={handleSaveAdmins}
                            disabled={loadingAdmins || selectedAdminIds.length === 0}
                          >
                            {loadingAdmins ? (
                              <>
                                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save size={16} />
                                Assign Admins ({selectedAdminIds.length})
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>


                {/* Activation Status Toggle */}
                <div style={{
                  background: theme === "light" ? "rgba(46, 124, 186, 0.05)" : "rgba(46, 124, 186, 0.1)",
                  border: theme === "light" ? "1px solid #2e7cba" : "1px solid rgba(46, 124, 186, 0.3)",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "24px",
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <div>
                      <h4 style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: theme === "light" ? "#0f172a" : "#ffffff",
                        marginBottom: "4px",
                      }}>
                        Company Status
                      </h4>
                      <p style={{
                        fontSize: "13px",
                        color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
                      }}>
                        {permissions.isActive ? "Company is currently active" : "Company is currently inactive"}
                      </p>
                    </div>
                    <div
                      style={styles.toggle(permissions.isActive)}
                      onClick={() => handleToggle('isActive')}
                    >
                      <div style={styles.toggleKnob(permissions.isActive)} />
                    </div>
                  </div>
                </div>
                {/* END OF ADDED CODE ↑ */}
                {Object.entries(moduleCategories).map(([category, modules]) => (
                  <div key={category}>
                    <h4 style={styles.sectionTitle}>{category}</h4>
                    {modules.map(module => (
                      <div key={module.key} style={styles.permissionItem}>
                        <span style={styles.permissionLabel}>{module.label}</span>
                        <div
                          style={styles.toggle(permissions[module.key])}
                          onClick={() => handleToggle(module.key)}
                        >
                          <div style={styles.toggleKnob(permissions[module.key])} />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                <button
                  style={styles.button}
                  onClick={handleSave}
                  disabled={saving}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 20px rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {saving ? <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={18} />}
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConfigurationManagement;